import { SettingsManager } from "@viamap/viamap2-common";

export interface LLMAnalyzerCallerInfo {
   userRef: string;
   userName: string;
   organizationId: string;
}

export interface LLMMetadata {
   document: string;
   aktId: string;
   matrikelData: {
      ejerlav: string;
      matnr: string;
      BFEnummer: string;
      adresse: string;
   }[];
   tekster: string[];
}

export interface LLMAnalysisResultItem {
   aktId: string;
   processing_method: string;
   reasoning: string;
   pages_kept: number[];
   original_pages: number;
   extracted_pages: number;
   summary: string;
   confidence: number;
}

export interface LLMAnalysisResult {
   items: LLMAnalysisResultItem[];
   total_processed: number;
   total_requested: number;
   timestamp: string;
}

export class ANNLLMAnalyzer {
   private baseUrl: string;
   private pdfServiceUrl: string;
   private pdfServiceApiKey: string;
   private token: string;
   private callerInfo?: LLMAnalyzerCallerInfo;

   constructor(callerInfo?: LLMAnalyzerCallerInfo) {
      this.baseUrl = SettingsManager.getSystemSetting("ann.servitutProcessorServiceHost");
      this.token = SettingsManager.getSystemSetting("jobs.jobServiceAPIKey");
      this.callerInfo = callerInfo;
      this.pdfServiceUrl = SettingsManager.getSystemSetting("ann.tinglysningApiGatewayHost");
      this.pdfServiceApiKey = SettingsManager.getSystemSetting("ann.tinglysningApiGatewayKey");
   }

   private getHeaders(): Record<string, string> {
      const headers: Record<string, string> = {
         "access-token": this.token
      };

      if (this.callerInfo) {
         headers["user-ref"] = this.callerInfo.userRef;
         headers["user-name"] = this.callerInfo.userName;
         headers["organization-id"] = this.callerInfo.organizationId;
      }

      return headers;
   }

   /** Returns the S3 URL for the akt file (XML). Use when opening link in new tab. */
   async getAktFileUrl(aktId: string): Promise<string> {
      if (!this.pdfServiceUrl || !this.pdfServiceApiKey) {
         throw new Error("Missing tinglysning API configuration");
      }
      const apiUrl = `${this.pdfServiceUrl.replace(/\/+$/, "")}/getTinglysningsAkt`;
      const response = await fetch(apiUrl, {
         method: "POST",
         headers: {
            "Content-Type": "application/json",
            "x-api-key": this.pdfServiceApiKey,
         },
         body: JSON.stringify({
            AktDokumentId: aktId,
            storeToFile: "true"
         }),
      });

      if (!response.ok) {
         const errorText = await response.text().catch(() => "Unknown error");
         if (response.status === 503 || /TLWebXML.*503|503.*TLWebXML/i.test(errorText)) {
            throw new Error("Tinglysning service is temporarily unavailable. Please try again later.");
         }
         throw new Error(`Tinglysning API error: ${response.status} - ${errorText.substring(0, 200)}`);
      }

      const raw = await response.json().catch(() => null) as { body?: string; url?: string } | null;
      if (!raw) throw new Error("Invalid API response");

      let inner: { url?: string } | null = null;
      if (typeof raw.body === "string") {
         try {
            inner = JSON.parse(raw.body) as { url?: string };
         } catch {
            inner = null;
         }
      }
      const fileUrl = raw.url ?? inner?.url;
      if (!fileUrl || typeof fileUrl !== "string") {
         throw new Error("API did not return file URL");
      }
      return fileUrl;
   }

   /** Extracts base64 PDF from SOAP EjendomIndskannetAktHentResultat XML. */
   private extractPdfFromSoapXml(xmlText: string): Blob {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlText, "application/xml");
      const ns = "http://rep.oio.dk/tinglysning.dk/schema/elektroniskakt/1/";
      let el = doc.getElementsByTagNameNS(ns, "IndskannetDokumentData")[0]
         ?? doc.getElementsByTagName("IndskannetDokumentData")[0];
      if (!el?.textContent?.trim()) {
         throw new Error("PDF data not found in SOAP response");
      }
      const binary = atob(el.textContent.trim());
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: "application/pdf" });
   }

   async fetchPdf(aktId: string): Promise<Blob> {
      const fileUrl = await this.getAktFileUrl(aktId);

      const fileResponse = await fetch(fileUrl);
      if (!fileResponse.ok) {
         throw new Error(
            `Failed to fetch file from storage (${fileResponse.status}). ` +
            `You can try opening directly: ${fileUrl}`
         );
      }
      const contentType = (fileResponse.headers.get("content-type") || "").toLowerCase();
      const text = await fileResponse.text();
      if (contentType.includes("xml") || text.trimStart().startsWith("<")) {
         return this.extractPdfFromSoapXml(text);
      }
      return new Blob([text], { type: "application/pdf" });
   }

   async analyzePdf(pdfBlob: Blob, metadata: LLMMetadata): Promise<LLMAnalysisResult> {
      const formData = new FormData();
      formData.append('files', pdfBlob, `${metadata.document || 'document.pdf'}`);
      formData.append('metadata', JSON.stringify([metadata]));

      const url = `${this.baseUrl}/process-pdf-batch?metadataOnly=true`;
      const headers = this.getHeaders();

      const response = await fetch(url, {
         method: 'POST',
         headers: headers,
         body: formData,
      });

      if (!response.ok) {
         const errorText = await response.text().catch(() => 'Unknown error');
         throw new Error(`LLM analysis failed: ${response.status} ${response.statusText} - ${errorText.substring(0, 200)}`);
      }

      return await response.json() as LLMAnalysisResult;
   }

   buildMetadata(
      servitut: {
         dokumentFilnavn?: string;
         aktId?: string;
         aktAliasId?: string;
         tekster?: string[];
         tekstSummarisk?: string;
      },
      stamOplysninger?: {
         indskannetAktDokumentId?: string;
         adresse?: {
            streetName?: string;
            buildingIdentifier?: string;
            postCode?: string;
            districtName?: string;
         };
      },
      xmlDoc?: Document | null,
      viamapTingBog?: any,
      propertyData?: any
   ): LLMMetadata {
      const aktIdForMetadata = (servitut?.aktId && servitut.aktId.length > 0)
         ? servitut.aktId
         : (servitut?.aktAliasId && servitut.aktAliasId.length > 0)
            ? servitut.aktAliasId
            : 'unknown';

      const documentFilename = servitut?.dokumentFilnavn && servitut.dokumentFilnavn.length > 0
         ? servitut.dokumentFilnavn
         : aktIdForMetadata;

      const matrikelData: LLMMetadata['matrikelData'] = [];

      if (propertyData) {
         try {
            const getEjerlavName = (ejerlavskode: number | string): string => {
               if (!ejerlavskode) return '';
               const code = typeof ejerlavskode === 'string' ? parseInt(ejerlavskode, 10) : ejerlavskode;
               if (propertyData.Ejerlav?.kode === code) {
                  return propertyData.Ejerlav.navn || '';
               }
               if (propertyData.BBRSFE && Array.isArray(propertyData.BBRSFE)) {
                  for (const sfe of propertyData.BBRSFE) {
                     if (sfe.properties?.jordstykke && Array.isArray(sfe.properties.jordstykke)) {
                        for (const jordstykke of sfe.properties.jordstykke) {
                           if (jordstykke.properties?.ejerlavskode === code) {
                              return jordstykke.properties.ejerlavsnavn || '';
                           }
                        }
                     }
                  }
               }
               return '';
            };

            if (propertyData.BBRSFE && Array.isArray(propertyData.BBRSFE)) {
               for (const sfe of propertyData.BBRSFE) {
                  const bfe = sfe.properties?.BFEnummer ? String(sfe.properties.BFEnummer) : '';
                  if (sfe.properties?.jordstykke && Array.isArray(sfe.properties.jordstykke)) {
                     for (const jordstykke of sfe.properties.jordstykke) {
                        const ejerlav = jordstykke.properties?.ejerlavsnavn || '';
                        const matnr = jordstykke.properties?.matrikelnummer ? String(jordstykke.properties.matrikelnummer).replace(/^0*/, '') : '';
                        const ejerlavskode = jordstykke.properties?.ejerlavskode;

                        if (matnr) {
                           const finalEjerlav = ejerlav || (ejerlavskode ? getEjerlavName(ejerlavskode) : '');

                           matrikelData.push({
                              ejerlav: finalEjerlav,
                              matnr,
                              BFEnummer: bfe,
                              adresse: propertyData.ViamapEjendomData?.ejdAdresse || (stamOplysninger?.adresse
                                 ? `${stamOplysninger.adresse.streetName || ''} ${stamOplysninger.adresse.buildingIdentifier || ''}, ${stamOplysninger.adresse.postCode || ''} ${stamOplysninger.adresse.districtName || ''}`.trim()
                                 : '')
                           });
                        }
                     }
                  }
               }
            }

            if (matrikelData.length === 0 && propertyData.ViamapEjendomData?.sag?.matrikler && Array.isArray(propertyData.ViamapEjendomData.sag.matrikler)) {
               const matrikler = propertyData.ViamapEjendomData.sag.matrikler;
               const bfe = String(propertyData.ViamapEjendomData.sag.BFEnr || '');
               const ejdAdresse = propertyData.ViamapEjendomData.ejdAdresse || '';

               for (const matrikel of matrikler) {
                  const matnr = String(matrikel.matrikelnummer || '').replace(/^0*/, '');
                  const ejerlavskode = matrikel.ejerlavskode;
                  let ejerlav = matrikel.ejerlavsnavn || '';
                  if (!ejerlav && ejerlavskode) {
                     ejerlav = getEjerlavName(ejerlavskode);
                  }

                  if (matnr) {
                     matrikelData.push({
                        ejerlav,
                        matnr,
                        BFEnummer: bfe,
                        adresse: ejdAdresse || (stamOplysninger?.adresse
                           ? `${stamOplysninger.adresse.streetName || ''} ${stamOplysninger.adresse.buildingIdentifier || ''}, ${stamOplysninger.adresse.postCode || ''} ${stamOplysninger.adresse.districtName || ''}`.trim()
                           : '')
                     });
                  }
               }
            }

            if (matrikelData.length === 0 && propertyData.Mat2) {
               const mat2 = propertyData.Mat2;
               const matnr = mat2.matrikelnummer ? String(mat2.matrikelnummer).replace(/^0*/, '') : '';
               const ejerlavskode = mat2.ejerlavskode;
               const bfe = mat2.samletfastejendomlokalid || '';

               if (matnr) {
                  const ejerlav = ejerlavskode ? getEjerlavName(ejerlavskode) : '';

                  matrikelData.push({
                     ejerlav,
                     matnr,
                     BFEnummer: bfe,
                     adresse: propertyData.ViamapEjendomData?.ejdAdresse || (stamOplysninger?.adresse
                        ? `${stamOplysninger.adresse.streetName || ''} ${stamOplysninger.adresse.buildingIdentifier || ''}, ${stamOplysninger.adresse.postCode || ''} ${stamOplysninger.adresse.districtName || ''}`.trim()
                        : '')
                  });
               }
            }
         } catch (error) {
         }
      }

      if (matrikelData.length === 0 && xmlDoc) {
         try {
            const ejendomme = Array.from(xmlDoc.getElementsByTagName("FastEjendomMedRettigheder"));

            for (const ejendom of ejendomme) {
               const bfe = ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || "";
               const matrikler = Array.from(ejendom.getElementsByTagName("Matrikel"));

               for (const matrikel of matrikler) {
                  const ejerlav = matrikel.getElementsByTagName("CadastralDistrictName")[0]?.textContent || "";
                  const matnr = String(matrikel.getElementsByTagName("Matrikelnummer")[0]?.textContent ?? "").replace(/^0*/, "");

                  matrikelData.push({
                     ejerlav,
                     matnr,
                     BFEnummer: bfe,
                     adresse: stamOplysninger?.adresse
                        ? `${stamOplysninger.adresse.streetName || ''} ${stamOplysninger.adresse.buildingIdentifier || ''}, ${stamOplysninger.adresse.postCode || ''} ${stamOplysninger.adresse.districtName || ''}`.trim()
                        : ''
                  });
               }
            }
         } catch (error) {
         }
      }

      if (matrikelData.length === 0) {
         const adresse = propertyData?.ViamapEjendomData?.ejdAdresse
            || (stamOplysninger?.adresse
               ? `${stamOplysninger.adresse.streetName || ''} ${stamOplysninger.adresse.buildingIdentifier || ''}, ${stamOplysninger.adresse.postCode || ''} ${stamOplysninger.adresse.districtName || ''}`.trim()
               : '');

         if (adresse) {
            matrikelData.push({
               ejerlav: '',
               matnr: '',
               BFEnummer: propertyData?.ViamapEjendomData?.sag?.BFEnr ? String(propertyData.ViamapEjendomData.sag.BFEnr) : '',
               adresse
            });
         }
      }

      const tekster: string[] = [];
      if (servitut?.tekster && Array.isArray(servitut.tekster)) {
         tekster.push(...servitut.tekster);
      }
      if (servitut?.tekstSummarisk) {
         tekster.push(servitut.tekstSummarisk);
      }

      return {
         document: `${documentFilename}.pdf`,
         aktId: aktIdForMetadata,
         matrikelData,
         tekster
      };
   }

   getAktIdForPdf(
      servitut?: {
         dokumentFilnavn?: string;
      },
      stamOplysninger?: {
         indskannetAktDokumentId?: string;
      }
   ): string | null {
      if (servitut?.dokumentFilnavn && servitut.dokumentFilnavn.length > 0) {
         return servitut.dokumentFilnavn;
      }
      if (stamOplysninger?.indskannetAktDokumentId) {
         return stamOplysninger.indskannetAktDokumentId;
      }
      return null;
   }
}

let analyzerInstance: ANNLLMAnalyzer | null = null;

export function getANNLLMAnalyzer(): ANNLLMAnalyzer {
   if (!analyzerInstance) {
      analyzerInstance = new ANNLLMAnalyzer();
   }
   return analyzerInstance;
}

export function createANNLLMAnalyzer(callerInfo: LLMAnalyzerCallerInfo): ANNLLMAnalyzer {
   return new ANNLLMAnalyzer(callerInfo);
}
